import React, { useEffect, useRef, useState } from "react";
import {
  Stack,
  Group,
  TextInput,
  NumberInput,
  Textarea,
  TagsInput,
  Collapse,
  UnstyledButton,
  Text,
  Button,
  ActionIcon,
  Tooltip,
  Box,
  Image,
  FileButton,
  SimpleGrid,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconTrash,
  IconWand,
  IconPhoto,
  IconUpload,
  IconLanguage,
  IconList,
  IconCode,
  IconQuestionMark,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import type { MenuItem } from "./types";
import { getItemCategories } from "./hooks/useMenuV2";
import { MenuItemIngredientsV2 } from "../../../components/MenuItemIngredientsV2";
import {
  uploadMenuItemImage,
  deleteMenuItemImage,
} from "../../../../services/imageService";
import api from "../../../../services/api";

interface Props {
  item: MenuItem;
  onUpdate: (itemId: string, updates: Partial<MenuItem>) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  title,
  icon,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
      <UnstyledButton
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "10px 0",
          width: "100%",
        }}
      >
        <Group gap={6} c="dimmed">
          {open ? (
            <IconChevronDown size={14} />
          ) : (
            <IconChevronRight size={14} />
          )}
          {icon}
          <Text size="sm" fw={500}>
            {title}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        {/* Lazy-mount children so sections like Ingredients & FAQ don't fire
            their fetches until the user actually opens the section. */}
        {open && <Box pb="md">{children}</Box>}
      </Collapse>
    </Box>
  );
};

// Local input that autosaves on blur, using draft state to avoid re-renders
// of the parent on every keystroke.
function useAutoSaveField<T>(initial: T, save: (v: T) => Promise<void>) {
  const [value, setValue] = useState<T>(initial);
  const initialRef = useRef(initial);
  useEffect(() => {
    initialRef.current = initial;
    setValue(initial);
  }, [initial]);
  const commit = async () => {
    if (value === initialRef.current) return;
    try {
      await save(value);
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Save failed",
        message: e?.response?.data?.error || e?.message || "Unknown error",
      });
      setValue(initialRef.current);
    }
  };
  return { value, setValue, commit };
}

export const ItemEditor: React.FC<Props> = ({ item, onUpdate, onDelete }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Basic fields with autosave-on-blur
  const name = useAutoSaveField(item.name || "", (v) =>
    onUpdate(item.id, { name: v })
  );
  const description = useAutoSaveField(item.description || "", (v) =>
    onUpdate(item.id, { description: v })
  );
  const price = useAutoSaveField<number | string | null>(
    item.price ?? null,
    (v) => onUpdate(item.id, { price: v as any })
  );
  const categories = useAutoSaveField<string[]>(
    getItemCategories(item),
    (v) => onUpdate(item.id, { categories: v, category: v[0] ?? null } as any)
  );

  // English translation fields, stored in props.translations.en
  const props = (item.props as any) || {};
  const enName = (props?.translations?.en?.name?.text as string) || "";
  const enDescription =
    (props?.translations?.en?.description?.text as string) || "";
  const enCategories =
    (props?.translations?.en?.categoryList as string[]) ||
    (props?.translations?.en?.category?.text
      ? [props.translations.en.category.text as string]
      : []);

  const translationName = useAutoSaveField(enName, (v) =>
    onUpdate(item.id, {
      props: {
        ...props,
        translations: {
          ...(props.translations || {}),
          en: {
            ...((props.translations || {}).en || {}),
            name: { text: v },
          },
        },
      } as any,
    } as Partial<MenuItem>)
  );
  const translationDescription = useAutoSaveField(enDescription, (v) =>
    onUpdate(item.id, {
      props: {
        ...props,
        translations: {
          ...(props.translations || {}),
          en: {
            ...((props.translations || {}).en || {}),
            description: { text: v },
          },
        },
      } as any,
    } as Partial<MenuItem>)
  );
  const translationCategories = useAutoSaveField<string[]>(enCategories, (v) =>
    onUpdate(item.id, {
      props: {
        ...props,
        translations: {
          ...(props.translations || {}),
          en: {
            ...((props.translations || {}).en || {}),
            categoryList: v,
            category: { text: v[0] ?? "" },
          },
        },
      } as any,
    } as Partial<MenuItem>)
  );

  // Image
  const [imageBusy, setImageBusy] = useState(false);
  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setImageBusy(true);
    try {
      const res = await uploadMenuItemImage(item.id, file);
      await onUpdate(item.id, {
        imageUrl: res.data.url,
        cloudinaryPublicId: res.data.publicId,
      });
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Upload failed",
        message: e?.message || "Unknown error",
      });
    } finally {
      setImageBusy(false);
    }
  };
  const handleImageDelete = async () => {
    setImageBusy(true);
    try {
      await deleteMenuItemImage(item.id);
      await onUpdate(item.id, { imageUrl: null, cloudinaryPublicId: null });
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Delete failed",
        message: e?.message || "Unknown error",
      });
    } finally {
      setImageBusy(false);
    }
  };

  // Per-item AI actions
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [faqLoading, setFaqLoading] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);

  const runItemAction = async (
    endpoint: string,
    setLoading: (b: boolean) => void,
    label: string
  ) => {
    setLoading(true);
    try {
      await api.post(endpoint, {});
      notifications.show({
        color: "green",
        title: `${label} queued`,
        message: "Updates will appear when the job completes.",
      });
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: `${label} failed`,
        message: e?.response?.data?.error || e?.message || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Raw props textarea
  const rawProps = useAutoSaveField(
    JSON.stringify(item.props ?? {}, null, 2),
    async (v) => {
      let parsed: unknown;
      try {
        parsed = v ? JSON.parse(v) : {};
      } catch {
        throw new Error("Invalid JSON");
      }
      await onUpdate(item.id, { props: parsed as any });
    }
  );

  return (
    <Box
      p="md"
      style={{
        background: "var(--mantine-color-gray-0)",
        borderRadius: 8,
      }}
    >
      <Stack gap="sm">
        {/* Basic */}
        <SimpleGrid cols={isMobile ? 1 : 2}>
          <TextInput
            label="Name"
            value={name.value}
            onChange={(e) => name.setValue(e.currentTarget.value)}
            onBlur={name.commit}
          />
          <NumberInput
            label="Price"
            value={price.value as number | string}
            onChange={(v) => price.setValue(v as any)}
            onBlur={price.commit}
            decimalScale={2}
            allowNegative={false}
          />
        </SimpleGrid>
        <TagsInput
          label="Categories"
          value={categories.value}
          onChange={(v) => {
            categories.setValue(v);
          }}
          onBlur={categories.commit}
          placeholder="Add a category…"
        />
        <Textarea
          label="Description"
          value={description.value}
          onChange={(e) => description.setValue(e.currentTarget.value)}
          onBlur={description.commit}
          autosize
          minRows={2}
        />

        {/* Inline AI quick actions */}
        <Group gap="xs" wrap="wrap">
          <Button
            size="compact-xs"
            variant="default"
            leftSection={<IconWand size={12} />}
            loading={analysisLoading}
            onClick={() =>
              runItemAction(
                `/menu-items/${item.id}/analysis`,
                setAnalysisLoading,
                "Analysis"
              )
            }
          >
            Redo analysis
          </Button>
          <Button
            size="compact-xs"
            variant="default"
            leftSection={<IconQuestionMark size={12} />}
            loading={faqLoading}
            onClick={() =>
              runItemAction(
                `/menu-items/${item.id}/faqs`,
                setFaqLoading,
                "FAQ"
              )
            }
          >
            Generate FAQ
          </Button>
          <Button
            size="compact-xs"
            variant="default"
            leftSection={<IconPhoto size={12} />}
            loading={imageGenLoading}
            onClick={() =>
              runItemAction(
                `/menu-items/${item.id}/image`,
                setImageGenLoading,
                "AI image"
              )
            }
          >
            Generate AI image
          </Button>
          <Box style={{ flex: 1 }} />
          <Tooltip label="Delete item">
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => {
                if (window.confirm(`Delete "${item.name}"?`)) onDelete(item.id);
              }}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Sections */}
        <Section title="Translations (EN)" icon={<IconLanguage size={14} />}>
          <Stack gap="sm">
            <TextInput
              label="Name"
              value={translationName.value}
              onChange={(e) => translationName.setValue(e.currentTarget.value)}
              onBlur={translationName.commit}
            />
            <TagsInput
              label="Categories"
              value={translationCategories.value}
              onChange={(v) => translationCategories.setValue(v)}
              onBlur={translationCategories.commit}
            />
            <Textarea
              label="Description"
              value={translationDescription.value}
              onChange={(e) =>
                translationDescription.setValue(e.currentTarget.value)
              }
              onBlur={translationDescription.commit}
              autosize
              minRows={2}
            />
          </Stack>
        </Section>

        <Section title="Ingredients & FAQ" icon={<IconList size={14} />}>
          <MenuItemIngredientsV2 menuItemId={item.id} />
        </Section>

        <Section title="Image" icon={<IconPhoto size={14} />}>
          <Stack gap="sm">
            {item.imageUrl ? (
              <Image
                src={item.imageUrl}
                alt={item.name}
                radius="md"
                style={{ maxWidth: 320 }}
              />
            ) : (
              <Text size="sm" c="dimmed">
                No image
              </Text>
            )}
            <Group>
              <FileButton accept="image/*" onChange={handleUpload}>
                {(p) => (
                  <Button
                    {...p}
                    leftSection={<IconUpload size={14} />}
                    size="xs"
                    variant="default"
                    loading={imageBusy}
                  >
                    {item.imageUrl ? "Replace" : "Upload"}
                  </Button>
                )}
              </FileButton>
              {item.imageUrl && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleImageDelete}
                  loading={imageBusy}
                >
                  Remove
                </Button>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              Recommended size: 1600 × 900 px (16:9)
            </Text>
          </Stack>
        </Section>

        <Section title="Raw props (JSON)" icon={<IconCode size={14} />}>
          <Textarea
            value={rawProps.value}
            onChange={(e) => rawProps.setValue(e.currentTarget.value)}
            onBlur={rawProps.commit}
            autosize
            minRows={6}
            styles={{ input: { fontFamily: "Monaco, monospace", fontSize: 12 } }}
          />
        </Section>
      </Stack>
    </Box>
  );
};
